import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useAuth } from './AuthContext';
import Spinner from './Components/Spinner';
import { SearchResultCard } from './SearchResults';
import { getVehicleCanonicalUrl, getVehiclePath } from './Utils/vehicleUrls';

const DEFAULT_RADIUS = '25';

function toDisplayName(slug) {
  return String(slug || '')
    .split('-')
    .filter(Boolean)
    .map((part) => {
      if (/^[a-z]\d/i.test(part) || part.length <= 2) {
        return part.toUpperCase();
      }

      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('-');
}

function buildSearch(make, model, zip, radius) {
  const params = new URLSearchParams({ make, model, page: '1' });

  if (zip) params.set('zip', zip);
  if (radius) params.set('radius', radius);

  return `/search?${params.toString()}`;
}

function UsedCarsLanding() {
  const { make: makeSlug, model: modelSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cars, setCars] = useState([]);
  const [savedCarIds, setSavedCarIds] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(true);

  const make = useMemo(() => toDisplayName(makeSlug), [makeSlug]);
  const model = useMemo(() => toDisplayName(modelSlug), [modelSlug]);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const page = Number(searchParams.get('page') || 1);
  const zip = searchParams.get('zip') || '';
  const radius = searchParams.get('radius') || (zip ? DEFAULT_RADIUS : '');
  const title = `Used ${make} ${model} for Sale`;
  const metaTitle = `${title} - Compare Prices & Sold Comps | Meshum`;
  const intro = `Browse used ${make} ${model} listings, compare asking prices, view Meshum market insights, and find cars priced below estimate.`;
  const canonicalUrl = `https://meshum.com/used-cars/${makeSlug}/${modelSlug}`;
  const searchUrl = buildSearch(make, model, zip, radius);

  useEffect(() => {
    if (!user?.id) return;

    fetch(`${process.env.REACT_APP_API_BASE}/saved/${user.id}`)
      .then((res) => res.json())
      .then((data) => setSavedCarIds(data))
      .catch((err) => console.error('Failed to fetch saved cars:', err));
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams({ make, model, page: String(page) });

    if (zip) params.set('zip', zip);
    if (radius) params.set('radius', radius);

    setLoading(true);
    fetch(`${process.env.REACT_APP_API_BASE}/cars/active?${params.toString()}`)
      .then((res) => {
        if (!res.ok) throw new Error('Inventory request failed');
        return res.json();
      })
      .then((data) => {
        setCars(data?.results || []);
        setTotalPages(data?.totalPages || 1);
        setTotalResults(data?.totalResults || 0);
      })
      .catch(() => {
        setCars([]);
        setTotalPages(1);
        setTotalResults(0);
      })
      .finally(() => setLoading(false));
  }, [make, model, page, radius, zip]);

  const handleCarClick = (car) => {
    navigate(getVehiclePath(car), {
      state: {
        from: { pathname: location.pathname, search: location.search },
      },
    });
  };

  const handleToggleSavedCar = async (carId, currentlySaved) => {
    if (!user?.id) return { ok: false, reason: 'auth' };

    try {
      const method = currentlySaved ? 'DELETE' : 'POST';
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/saved`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, carId }),
      });

      if (!res.ok) return { ok: false };

      setSavedCarIds((prev) => {
        if (currentlySaved) return prev.filter((id) => id !== carId);
        return Array.from(new Set([...prev, carId]));
      });

      return { ok: true };
    } catch (err) {
      console.error('Error toggling saved state:', err);
      return { ok: false };
    }
  };

  const goToPage = (newPage) => {
    const params = new URLSearchParams(location.search);
    params.set('page', newPage);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const itemListJson = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: title,
    numberOfItems: cars.length,
    itemListElement: cars.map((car, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: getVehicleCanonicalUrl(car),
      item: {
        '@type': 'Vehicle',
        name: `${car.year} ${car.make} ${car.model}`.replace(/\s+/g, ' ').trim(),
        image: car.imageUrl,
        offers: {
          '@type': 'Offer',
          price: car.price,
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url: getVehicleCanonicalUrl(car),
        },
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={intro} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={intro} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        {cars.length > 0 && (
          <script type="application/ld+json">{JSON.stringify(itemListJson)}</script>
        )}
      </Helmet>

      <main className="ui-page bg-slate-50">
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
                Meshum inventory
              </div>
              <h1 className="mt-2 text-2xl font-semibold leading-tight text-slate-950 sm:text-3xl">
                {title}
              </h1>
              <p className="mt-2 text-sm leading-6 text-slate-600 sm:text-base">{intro}</p>
              {zip && (
                <div className="mt-3 flex flex-wrap gap-2 text-sm text-slate-600">
                  <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 font-medium">
                    ZIP: {zip}
                  </span>
                  <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 font-medium">
                    {radius === '9999' ? 'Nationwide' : `${radius} mi`}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row lg:items-end">
              <Link to={searchUrl} className="btn-primary">
                Refine search
              </Link>
            </div>
          </div>

          <div className="mt-4 border-t border-slate-200 pt-4 text-sm text-slate-600">
            {loading
              ? 'Loading listings...'
              : `${totalResults.toLocaleString()} ${totalResults === 1 ? 'listing' : 'listings'}`}
          </div>
        </section>

        {loading ? (
          <Spinner />
        ) : cars.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">No inventory available</h2>
            <p className="mt-2 text-sm text-slate-600">
              There are no active used {make} {model} listings on Meshum right now.
            </p>
            <Link to={searchUrl} className="btn-primary mt-5 inline-flex">
              Search nearby inventory
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {cars.map((car) => (
              <SearchResultCard
                key={car.id}
                car={{ ...car, scrollId: `car-${car.id}` }}
                saved={savedCarIds.includes(car.id)}
                userId={user?.id}
                onOpen={handleCarClick}
                onToggleSaved={handleToggleSavedCar}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && !loading && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-center">
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
              className="btn-primary bg-teal-600 hover:bg-teal-700"
              type="button"
            >
              <FaChevronLeft className="mr-2 text-xs" />
              Prev
            </button>
            <span className="text-sm font-semibold text-slate-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
              className="btn-primary bg-teal-600 hover:bg-teal-700"
              type="button"
            >
              Next
              <FaChevronRight className="ml-2 text-xs" />
            </button>
          </div>
        )}
      </main>
    </>
  );
}

export default UsedCarsLanding;
